import React, {useEffect, useCallback, useRef} from 'react';
import {Dialog, Button, Icon} from '@gravity-ui/uikit';
import {TransformWrapper, TransformComponent, ReactZoomPanPinchRef} from 'react-zoom-pan-pinch';
import {Xmark, MagnifierPlus, MagnifierMinus, ArrowDownToLine} from '@gravity-ui/icons';
import styles from './ImageViewer.module.scss';

interface ImageFile {
  fileName: string;
  url: string;
}

interface ImageViewerProps {
  open: boolean;
  onClose: () => void;
  images: ImageFile[];
  currentImageIndex: number;
  onImageChange: (index: number) => void;
}

export const ImageViewer = ({open, onClose, images, currentImageIndex, onImageChange}: ImageViewerProps) => {
  const currentImage = images[currentImageIndex];
  const transformRef = useRef<ReactZoomPanPinchRef>(null);

  // Обработка нажатий клавиш
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!open) return;

      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (currentImageIndex > 0) {
            onImageChange(currentImageIndex - 1);
          }
          break;
        case 'ArrowRight':
          if (currentImageIndex < images.length - 1) {
            onImageChange(currentImageIndex + 1);
          }
          break;
      }
    },
    [open, currentImageIndex, images.length, onClose, onImageChange],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Функция скачивания изображения
  const handleDownload = async () => {
    if (!currentImage) return;

    try {
      const response = await fetch(currentImage.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = currentImage.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Ошибка при скачивании изображения:', error);
    }
  };

  // Функции управления масштабом
  const handleZoomIn = () => {
    if (transformRef.current) {
      transformRef.current.zoomIn(0.3);
    }
  };

  const handleZoomOut = () => {
    if (transformRef.current) {
      transformRef.current.zoomOut(0.3);
    }
  };

  if (!currentImage) return null;

  return (
    <Dialog open={open} onClose={onClose} size="m" className={styles.dialog} hasCloseButton={false}>
      <div className={styles.container}>
        {/* Заголовок с кнопками управления */}
        <div className={styles.header}>
          <div className={styles.imageInfo}>
            <span className={styles.fileName}>{currentImage.fileName}</span>
          </div>
          <div className={styles.controls}>
            <Button onClick={handleDownload} size="l" view="flat">
              <Icon data={ArrowDownToLine} size={16} />
            </Button>
            <Button onClick={onClose} size="l" view="flat">
              <Icon data={Xmark} size={16} />
            </Button>
          </div>
        </div>

        {/* Основная область просмотра */}
        <div className={styles.imageContainer}>
          <TransformWrapper
            ref={transformRef}
            initialScale={1}
            minScale={0.1}
            maxScale={5}
            centerOnInit={true}
            wheel={{
              disabled: false,
              step: 0.2,
            }}
            doubleClick={{
              disabled: false,
              mode: 'reset',
            }}
            panning={{
              velocityDisabled: false,
            }}>
            <TransformComponent wrapperClass={styles.transformWrapper} contentClass={styles.transformContent}>
              <img src={currentImage.url} alt={currentImage.fileName} className={styles.image} draggable={false} />
            </TransformComponent>
          </TransformWrapper>

          {/* Кнопки управления масштабом */}
          <div className={styles.zoomControls}>
            <Button onClick={handleZoomIn} size="l" view="normal">
              <Icon data={MagnifierPlus} size={16} />
            </Button>
            <Button onClick={handleZoomOut} size="l" view="normal">
              <Icon data={MagnifierMinus} size={16} />
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
